// The PC top bar's search: "Search pages, boats, SAP". One field finds pages (the nav menu's type-to-find,
// same visibility rules) and boats from the overview query already in the cache for the active season
// (SAP, owner name or model). It never fetches on its own. A page opens itself; a boat opens the Overview
// with its SAP as ?q=. "/" focuses the field from anywhere outside a text input.
// ARIA: an editable combobox with a listbox popup (APG), arrow keys + Enter, Escape closes then clears.

import { useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { OverviewVessel } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { useSeason } from '../season/SeasonContext.js';
import { useNavModel, type NavHit } from '../app/useNavModel.js';
import { fold } from '../lib/search.js';
import type { OverviewResponse } from '../features/hooks.js';
import { IconSearch } from './icons.js';
import { JourneyStrip } from './JourneyStrip.js';

const MAX_PAGES = 7;
const MAX_BOATS = 6;

type Hit =
  | { kind: 'page'; key: string; hit: NavHit }
  | { kind: 'boat'; key: string; v: OverviewVessel };

/** a boat matches on its SAP (digits anywhere), owner name or model, ignoring case and accents; SAP
 *  prefix hits rank first. */
function findBoats(vessels: OverviewVessel[], query: string): OverviewVessel[] {
  const q = fold(query.trim());
  if (!q) return [];
  const scored: Array<[number, OverviewVessel]> = [];
  for (const v of vessels) {
    const sap = fold(v.sap);
    const name = fold(v.name);
    const model = fold(v.model);
    let score = -1;
    if (sap.startsWith(q)) score = 0;
    else if (model === q) score = 1;
    else if (sap.includes(q)) score = 2;
    else if (name.includes(q)) score = 3;
    else if (model.includes(q)) score = 4;
    if (score >= 0) scored.push([score, v]);
  }
  return scored.sort((a, b) => a[0] - b[0]).slice(0, MAX_BOATS).map(([, v]) => v);
}

export function TopSearch() {
  const { can, canSeePath } = useAuth();
  const { activeSeason } = useSeason();
  const { findPages } = useNavModel();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const optId = (i: number) => `${listId}-o${i}`;

  // boats only for someone who may open the Overview; read from the cache, never fetched from here
  const boatsAllowed = can('overview:read') && canSeePath('/overview');
  const vessels = boatsAllowed ? qc.getQueryData<OverviewResponse>(['overview', activeSeason])?.vessels : undefined;

  const pages = findPages(q).slice(0, MAX_PAGES);
  const boats = vessels ? findBoats(vessels, q) : [];
  const hits: Hit[] = [
    ...pages.map((hit) => ({ kind: 'page' as const, key: `p:${hit.leaf.path ?? hit.leaf.external ?? hit.leaf.label}`, hit })),
    ...boats.map((v) => ({ kind: 'boat' as const, key: `b:${v.rowId}:${v.sap}`, v })),
  ];
  const showPop = open && q.trim().length > 0;
  const activeIdx = hits.length ? Math.min(active, hits.length - 1) : -1;

  // "/" jumps to the search from anywhere that is not already a text field
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const reset = () => { setQ(''); setOpen(false); setActive(0); };
  const choose = (h: Hit | undefined) => {
    if (!h) return;
    if (h.kind === 'page') {
      const leaf = h.hit.leaf;
      if (leaf.external) window.open(leaf.external, '_blank', 'noopener,noreferrer');
      else if (leaf.path) navigate(leaf.path);
    } else {
      // the Overview filters by this when it reads ?q= (see the hand-off notes in the revamp)
      navigate(`/overview?q=${encodeURIComponent(h.v.sap)}`);
    }
    reset();
    inputRef.current?.blur();
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!hits.length) return;
      setOpen(true);
      const d = e.key === 'ArrowDown' ? 1 : -1;
      setActive((a) => (Math.min(a, hits.length - 1) + d + hits.length) % hits.length);
    } else if (e.key === 'Enter') {
      if (showPop && activeIdx >= 0) { e.preventDefault(); choose(hits[activeIdx]); }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (showPop) setOpen(false);
      else if (q) setQ('');
      else inputRef.current?.blur();
    }
  };

  // keep the highlighted option in view while arrowing through a long list
  useEffect(() => {
    if (!showPop || activeIdx < 0) return;
    document.getElementById(`${listId}-o${activeIdx}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx, showPop, listId]);

  let n = -1; // running option index across both sections
  const count = hits.length;
  return (
    <div className="gsearch">
      <label className="gsearch-field">
        <IconSearch size={16} className="gsearch-ic" />
        <input
          ref={inputRef}
          className="gsearch-in"
          type="search"
          role="combobox"
          aria-label="Search pages, boats and SAP numbers"
          aria-autocomplete="list"
          aria-expanded={showPop}
          aria-controls={listId}
          aria-activedescendant={showPop && activeIdx >= 0 ? optId(activeIdx) : undefined}
          placeholder="Search pages, boats, SAP"
          autoComplete="off"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); setActive(0); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
        />
        {!q && <kbd className="gsearch-kbd" aria-hidden="true">/</kbd>}
      </label>
      <span className="sr-only" role="status" aria-live="polite">
        {showPop ? (count ? `${count} result${count === 1 ? '' : 's'}` : 'No results') : ''}
      </span>
      {showPop && (
        // mousedown is prevented so a click lands before the field's blur closes the list
        <div className="gsearch-pop" onMouseDown={(e) => e.preventDefault()}>
          {count > 0 && (
            <div id={listId} role="listbox" aria-label="Search results">
              {pages.length > 0 && (
                <div role="group" aria-label="Pages" className="gsearch-sec">
                  <div className="gsearch-sec-hd" aria-hidden="true">Pages</div>
                  {pages.map((hit) => {
                    const i = ++n;
                    const h = hits[i]!;
                    return (
                      <div key={h.key} id={optId(i)} role="option" aria-selected={i === activeIdx}
                        className="gsearch-opt" onMouseEnter={() => setActive(i)} onClick={() => choose(h)}>
                        <span className="gsearch-opt-main">{hit.leaf.label}{hit.leaf.external ? ' (new tab)' : ''}</span>
                        <span className="gsearch-opt-meta">{hit.group}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              {boats.length > 0 && (
                <div role="group" aria-label="Boats" className="gsearch-sec">
                  <div className="gsearch-sec-hd" aria-hidden="true">Boats · season {activeSeason}</div>
                  {boats.map((v) => {
                    const i = ++n;
                    const h = hits[i]!;
                    return (
                      <div key={h.key} id={optId(i)} role="option" aria-selected={i === activeIdx}
                        className="gsearch-opt gsearch-boat" onMouseEnter={() => setActive(i)} onClick={() => choose(h)}>
                        <span className="gsearch-sap">{v.sap}</span>
                        <span className="gsearch-mdl">{v.model || '-'}</span>
                        <span className="gsearch-opt-main">{v.name || 'Unnamed boat'}</span>
                        <JourneyStrip vessel={v} size="sm" />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          {count === 0 && <div className="gsearch-empty">Nothing matches “{q.trim()}”.</div>}
          {boatsAllowed && !vessels && (
            <div className="gsearch-note">Boats appear once the fleet has loaded: open the Dashboard or the Overview.</div>
          )}
        </div>
      )}
    </div>
  );
}
